import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/Section";
import { CTASection } from "@/components/CTASection";
import { FAQ } from "@/components/FAQ";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/lib/site";

import { useSuspenseQuery } from "@tanstack/react-query";
import { listPublishedPosts } from "@/lib/content.functions";
import { listServices } from "@/lib/services.functions";
import { formatDateTimeBR } from "@/lib/format";
import { TEAM } from "@/lib/team";
import { REVIEWS } from "@/lib/reviews";
import { CATEGORIES } from "@/lib/blog";
import { seoTitle, seoDescription } from "@/lib/seo-meta";

const TITLE = seoTitle("Dedetizadora em Chapecó — Controle de Pragas");
const DESC = seoDescription(
  "Dedetizadora em Chapecó/SC especializada em baratas, ratos, cupins, formigas e insetos. Atendimento residencial, comercial e industrial com laudo e garantia.",
);

const faq = [
  { q: "Quais pragas vocês controlam em Chapecó?", a: "Fazemos dedetização de baratas, formigas, aranhas e escorpiões; desratização (ratos e camundongos); descupinização (cupim de madeira e de solo); e controle de moscas, pulgas e carrapatos, tanto em residências quanto em empresas." },
  { q: "O serviço é seguro para crianças e animais de estimação?", a: "Sim. Usamos produtos registrados no Ministério da Saúde e Anvisa, aplicados por técnicos com EPI. Explicamos o tempo mínimo de retorno ao ambiente conforme o produto usado — normalmente entre 2 e 4 horas depois da aplicação." },
  { q: "Vocês emitem laudo técnico e certificado?", a: "Sim. Ao final do serviço entregamos certificado de execução com data, produtos utilizados, número do lote e assinatura do responsável técnico — aceito pela Vigilância Sanitária de Chapecó para empresas de alimentação e saúde." },
  { q: "Como funciona a garantia?", a: "Cada serviço tem garantia contratual de 3 a 6 meses, dependendo do tipo de praga e do ambiente. Se a infestação voltar dentro do prazo, refazemos a aplicação sem custo adicional." },
  { q: "Quanto custa uma dedetização em Chapecó?", a: "O valor depende do tamanho do imóvel, tipo de praga e nível de infestação. Fazemos vistoria técnica gratuita, sem compromisso, e enviamos o orçamento por WhatsApp." },
  { q: "Quanto tempo demora o atendimento?", a: "Casos urgentes (escorpião, infestação grave em restaurante) atendemos no mesmo dia. Serviços comuns costumam ser agendados em até 48 horas úteis." },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "/" },
    ],
    links: [
      { rel: "canonical", href: `${SITE.baseUrl}/` },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["public-posts"],
        queryFn: () => listPublishedPosts(),
        staleTime: 60_000,
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["services-active"],
        queryFn: () => listServices(),
        staleTime: 300_000,
      }),
    ]);
  },
  component: Home,
});



const regionCities = [
  { s: "chapeco", n: "Chapecó" },{ s: "xaxim", n: "Xaxim" },{ s: "xanxere", n: "Xanxerê" },
  { s: "cordilheira-alta", n: "Cordilheira Alta" },{ s: "guatambu", n: "Guatambu" },{ s: "coronel-freitas", n: "Coronel Freitas" },
  { s: "nova-itaberaba", n: "Nova Itaberaba" },{ s: "seara", n: "Seara" },{ s: "concordia", n: "Concórdia" },
  { s: "pinhalzinho", n: "Pinhalzinho" },{ s: "sao-carlos", n: "São Carlos" },{ s: "aguas-de-chapeco", n: "Águas de Chapecó" },
  { s: "aguas-frias", n: "Águas Frias" },{ s: "nova-erechim", n: "Nova Erechim" },{ s: "quilombo", n: "Quilombo" },
  { s: "saudades", n: "Saudades" },{ s: "planalto-alegre", n: "Planalto Alegre" },{ s: "caxambu-do-sul", n: "Caxambu do Sul" },
  { s: "irati", n: "Irati" },{ s: "formosa-do-sul", n: "Formosa do Sul" },{ s: "jardinopolis", n: "Jardinópolis" },
  { s: "uniao-do-oeste", n: "União do Oeste" },
];

const why = [
  "Vistoria técnica gratuita no seu imóvel em Chapecó e região",
  "Produtos registrados no Ministério da Saúde e Anvisa",
  "Aplicação com EPI completo e técnicos treinados",
  "Certificado de execução aceito pela Vigilância Sanitária",
  "Garantia contratual de 3 a 6 meses",
  "Atendimento de emergência 24h para pragas críticas",
];

function Home() {
  const { data: dbPosts = [] } = useSuspenseQuery({
    queryKey: ["public-posts"],
    queryFn: () => listPublishedPosts(),
    staleTime: 60_000,
  });
  const { data: services = [] } = useSuspenseQuery({
    queryKey: ["services-active"],
    queryFn: () => listServices(),
    staleTime: 300_000,
  });
  const latestPosts = dbPosts.slice(0, 6);
  const highlightCats = CATEGORIES.slice(0, 6);

  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-12 pb-14 md:pt-20 md:pb-20">
          <div className="grid gap-10 lg:grid-cols-[1.15fr_1fr] items-center">
            <div>
              <span className="eyebrow">Chapecó · Oeste de Santa Catarina</span>
              <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-6xl">
                <span className="text-gradient">Dedetizadora em Chapecó</span> — sua casa e sua empresa livres de pragas.
              </h1>
              <p className="mt-5 text-muted-foreground md:text-lg">
                Controle profissional de baratas, ratos, cupins, formigas, aranhas e escorpiões. Equipe técnica, produtos registrados, garantia de serviço e laudo aceito pela Vigilância Sanitária.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={SITE.whatsappUrl} target="_blank" rel="noopener" className="btn-accent px-6 py-3">Orçamento no WhatsApp</a>
                <Link to="/contato" className="btn-ghost px-6 py-3">Agendar vistoria gratuita</Link>
              </div>
              <ul className="mt-8 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                {why.slice(0, 4).map((w) => (
                  <li key={w} className="flex items-start gap-2">
                    <span aria-hidden className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded bg-accent text-primary">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m5 12 5 5L20 7" /></svg>
                    </span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="hidden lg:block">
              <img
                src="/__l5e/assets-v1/e71d6672-c05c-4fa2-a933-eb7dc0626ddc/dedetizacao.jpg"
                alt="Técnico da Dedetizadora Chapecó aplicando controle de pragas em residência"
                loading="eager"
                className="rounded-2xl shadow-[var(--shadow-glow)] ring-1 ring-border"
                width={1200}
                height={800}
              />
            </div>
          </div>
        </div>
      </section>


      <Section>
        <SectionHeader eyebrow="o que fazemos" title="Nossos serviços" description="Soluções técnicas para residências, comércios, indústrias e condomínios em Chapecó e região." />
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <Reveal key={s.slug} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <Link to="/servicos/$slug" params={{ slug: s.slug }} className="card-glass group block h-full overflow-hidden p-0">
                {s.cover_image ? (
                  <div className="relative h-48 w-full overflow-hidden">
                    <img
                      src={s.cover_image}
                      alt={s.label}
                      loading="lazy"
                      width={600}
                      height={400}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-transparent" />
                  </div>
                ) : null}
                <div className="p-5">
                  <h3 className="text-lg font-semibold group-hover:text-primary transition-colors">{s.label}</h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{s.description}</p>
                  <span className="mt-4 inline-block text-xs font-semibold uppercase tracking-wide text-primary">ver detalhes →</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>


      <Section className="bg-[color:var(--color-sidebar)]">
        <SectionHeader eyebrow="onde atendemos" title="Áreas de atendimento" description="Atendemos Chapecó, bairros e cidades da região oeste de SC." />
        <ul className="mt-8 grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 text-sm">
          {regionCities.map((c) => (
            <li key={c.s}>
              <Link to="/$location/$service" params={{ location: c.s, service: "dedetizacao" }} className="block rounded border border-border bg-card px-3 py-2 hover:border-primary hover:text-primary transition-colors">{c.n} · SC</Link>
            </li>
          ))}
        </ul>
        <div className="mt-6 text-center">
          <Link to="/locais" className="btn-primary">Ver todos os bairros e cidades</Link>
        </div>
      </Section>

      {latestPosts.length > 0 && (
        <Section>
          <SectionHeader eyebrow="Blog" title="Últimos artigos sobre controle de pragas" description="Dicas, guias e informações sobre dedetização em Chapecó e região." />
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {latestPosts.map((p) => (
              <Link key={p.slug} to="/blog/$slug" params={{ slug: p.slug }} className="group card-glass block h-full overflow-hidden p-0">
                {p.cover_image || p.cover ? (
                  <img src={p.cover_image ?? p.cover} alt={p.title} loading="lazy" className="h-44 w-full object-cover" width={400} height={220} />
                ) : null}
                <div className="p-5">
                  <span className="eyebrow">{p.category}</span>
                  <h3 className="mt-2 text-base font-semibold group-hover:text-primary transition-colors">{p.title}</h3>
                  <p className="mt-2 text-xs text-muted-foreground">{p.published_at ? formatDateTimeBR(p.published_at) : ""}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/blog" className="btn-primary">Ver todos os artigos</Link>
          </div>
        </Section>
      )}

      <Section className="bg-[color:var(--color-sidebar)]">
        <SectionHeader eyebrow="Categorias" title="Assuntos que cobrimos no blog" />
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {highlightCats.map((c) => (
            <Link key={c} to="/blog" hash={`cat=${encodeURIComponent(c)}`} className="rounded border border-border bg-card px-4 py-2 text-sm font-medium hover:border-primary hover:text-primary transition-colors">
              {c}
            </Link>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeader eyebrow="Equipe técnica" title="Profissionais responsáveis pelo seu atendimento" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TEAM.map((m) => (
            <article key={m.name} className="card-glass flex h-full flex-col items-center text-center">
              <img src={m.photo} alt={`Foto de ${m.name}`} loading="lazy" width={96} height={96} className="h-24 w-24 rounded object-cover border-2 border-primary" />
              <h3 className="mt-5 text-lg font-semibold">{m.name}</h3>
              <p className="mt-1 text-xs uppercase tracking-wider font-semibold text-primary">{m.role}</p>
              <p className="mt-3 text-sm text-muted-foreground">{m.bio}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section className="bg-[color:var(--color-sidebar)]">
        <SectionHeader eyebrow="Clientes em Chapecó" title="O que dizem sobre nossos serviços" />
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {REVIEWS.slice(0, 6).map((r) => (
            <div key={r.name} className="card-glass">
              <div className="flex text-accent" aria-label="5 estrelas">{"★★★★★"}</div>
              <p className="mt-3 text-sm text-muted-foreground">"{r.text}"</p>
              <p className="mt-4 text-xs font-semibold text-foreground">{r.name}</p>
              <p className="text-[11px] text-muted-foreground">{r.neighborhood}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <FAQ items={faq} />
      </Section>

      <CTASection
        title="Precisa de dedetização em Chapecó agora?"
        text="Chame no WhatsApp, descreva a praga e receba o orçamento em poucos minutos. Vistoria técnica gratuita e sem compromisso."
        buttonLabel="Falar no WhatsApp"
      />
    </>
  );
}
